import Accordion from '@/common/components/Accordion'
import Button from '@/common/components/Button'
import Checkbox from '@/common/components/Checkbox'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import MaskedTextField from '@/common/components/MaskedTextField'
import Select from '@/common/components/Select'
import SelectController from '@/common/components/SelectController'
import TextField from '@/common/components/TextField'
import { percentageMask } from '@/common/constants/masks.constant'
import useDebounce from '@/common/hooks/useDebounce'
import { Mapper } from '@/common/services/mapper'
import { formatCurrency, formatPercentage } from '@/common/utils/formatters'
import { unFormatCurrency, unFormatPercentage } from '@/common/utils/unFormatters'
import { useFeeTemplates } from '@/features/feeTemplates/hooks/useFeeTemplates'
import { FeeTemplateData } from '@/features/feeTemplates/types/feeTemplates.type'
import usePrivateCompanyLead from '@/features/privateCompanyLeadsFunnel/hooks/usePrivateCompanyLead'
import { useUpdatePrivateCompanyLeadContract } from '@/features/privateCompanyLeadsFunnel/hooks/useUpdatePrivateCompanyLeadContractFee'
import { PrivateCompanyLeadsFunnelUpdateContractValidationSchema } from '@/features/privateCompanyLeadsFunnel/schemas/update.schema'
import { ContractData } from '@/features/privateCompanyLeadsFunnel/types/privateCompanyLead'
import { getPriceRuleOptions } from '@/features/publicCompanyLeadsFunnel/constants/priceRuleOptions.constant'
import { UseMutateFunction, useQueryClient } from '@tanstack/react-query'
import { XIcon } from 'lucide-react'
import { ChangeEvent, useMemo } from 'react'
import { Control, Controller, FieldErrors, UseFormRegister, UseFormWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'

interface ContractFeesProps {
  contract?: ContractData
  control: Control<PrivateCompanyLeadsFunnelUpdateContractValidationSchema, any>
  register: UseFormRegister<PrivateCompanyLeadsFunnelUpdateContractValidationSchema>
  watch: UseFormWatch<PrivateCompanyLeadsFunnelUpdateContractValidationSchema>
  errors: FieldErrors<PrivateCompanyLeadsFunnelUpdateContractValidationSchema>
  createPrivateCompanyLeadContractFee: UseMutateFunction<void, Error, { label: string; value: string }, unknown>
  createPrivateCompanyLeadContractFeeIsPending?: boolean
  deletePrivateCompanyLeadContactFee: UseMutateFunction<void, Error, string, unknown>
  disableFields?: boolean
}

export const ContractFees = ({
  contract,
  control,
  register,
  watch,
  errors,
  createPrivateCompanyLeadContractFee,
  createPrivateCompanyLeadContractFeeIsPending,
  deletePrivateCompanyLeadContactFee,
  disableFields,
}: ContractFeesProps) => {
  const { privateCompanyLeadId } = useParams()
  const queryClient = useQueryClient()

  const [searchFeeTemplate, setSearchFeeTemplate] = useDebounce('')

  const {
    isFetching: privateCompanyIsFetching
  } = usePrivateCompanyLead(privateCompanyLeadId || '')

  const priceRuleOptions = useMemo(
    () => getPriceRuleOptions({ isPrivateFuel: true }),
    []
  )

  const { data: feeTemplateOptions } = useFeeTemplates({
    filters: { search: searchFeeTemplate, category: 'private_company' },
    mapper: Mapper.mapToOptions<FeeTemplateData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const {
    mutate: updatePrivateCompanyLeadContract,
    isPending: updateContractFeeIsPending
  } = useUpdatePrivateCompanyLeadContract({
    privateCompanyLeadId,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', privateCompanyLeadId] })
    },
  })

  const submit = ({
    id,
    value,
    calculate_as,
    main_fee,
  }: {
    id: string
    value: string
    calculate_as: string
    main_fee?: boolean
  }) => {
    if (!/R\$|%/.test(value)) {
      return
    }

    const unFormatFunction = calculate_as === 'percentage' ? unFormatPercentage : unFormatCurrency
    value = `${unFormatFunction(value)}`

    updatePrivateCompanyLeadContract({ contract_fees_attributes: { id, value, main_fee } })
  }

  const submitMainFee = ({ id, main_fee }: { id: string; main_fee: boolean }) => {
    updatePrivateCompanyLeadContract({ contract_fees_attributes: { id, main_fee } })
  }

  return (
    <Accordion
      className="py-1"
      contentClassName="data-[state=open]:overflow-visible"
      triggerDisabled={updateContractFeeIsPending}
      head={<p>Regras e Tarifas</p>}
      body={
        <div className="flex flex-col gap-4">
          <Select
            label="Regra de Preço (Padrão)"
            defaultValue={priceRuleOptions.find((option) => option.value === contract?.default_fuel_price_rule)}
            options={priceRuleOptions}
            onChange={(newValue: any) => {
              if (newValue?.value) {
                updatePrivateCompanyLeadContract({ default_fuel_price_rule: newValue.value })
              }
            }}
            isDisabled={createPrivateCompanyLeadContractFeeIsPending || privateCompanyIsFetching || disableFields}
            isLoading={createPrivateCompanyLeadContractFeeIsPending || privateCompanyIsFetching}
          />
          <p>Tarifas padrão</p>
          <SelectController
            control={control}
            name="contract.contract_fees"
            placeholder="Pesquisar Tarifa"
            value={null}
            onInputChange={setSearchFeeTemplate}
            triggerOnChange={(newValue: { label: string; value: string }) => {
              createPrivateCompanyLeadContractFee(newValue)
            }}
            options={
              feeTemplateOptions?.filter((feeTemplate) => {
                return !contract?.contract_fees?.some(({ fee_template }) => fee_template.id === feeTemplate.value)
              }) || []
            }
            isDisabled={createPrivateCompanyLeadContractFeeIsPending || privateCompanyIsFetching || disableFields}
            isLoading={createPrivateCompanyLeadContractFeeIsPending || privateCompanyIsFetching}
          />
          {contract?.contract_fees?.map(({ id, fee_template, value, main_fee }, index) => (
            <div key={fee_template.id} className="flex gap-2">
              <Button
                type="button"
                variant="outline-error"
                className="size-[3.25rem] mt-5"
                onClick={() => deletePrivateCompanyLeadContactFee(id)}
                disabled={updateContractFeeIsPending || privateCompanyIsFetching || disableFields}
              >
                <XIcon />
              </Button>

              <div className="w-1/2 flex flex-col gap-y-2">
                <TextField label="Categoria" defaultValue={fee_template.display_name} disabled />

                {fee_template.calculate_as === 'percentage' && (
                  <Checkbox
                    label="Tarifa principal"
                    className="gap-2"
                    labelClassName="text-[10px] font-light italic"
                    defaultChecked={watch(`contract.contract_fees.${index}.main_fee`) || main_fee}
                    {...register(`contract.contract_fees.${index}.main_fee`)}
                    errorMessage={errors?.contract?.contract_fees?.[index]?.main_fee?.message || undefined}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      register(`contract.contract_fees.${index}.main_fee`).onChange(event)
                      submitMainFee({
                        id,
                        main_fee: event.target.checked,
                      })
                    }}
                    disabled={updateContractFeeIsPending || privateCompanyIsFetching || disableFields}
                  />
                )}
              </div>

              <Controller
                name={`contract.contract_fees.${index}.value`}
                control={control}
                render={({ field }) => (
                  <>
                    {fee_template.calculate_as === "fixed" ? (
                      <CurrencyTextField
                        label="Valor"
                        className="w-1/2"
                        {...field}
                        value={watch(`contract.contract_fees.${index}.value`) || formatCurrency(value)}
                        errorMessage={errors?.contract?.contract_fees?.[index]?.value?.message || undefined}
                        disabled={updateContractFeeIsPending || privateCompanyIsFetching || disableFields}
                        onBlur={(e) => {
                          field.onBlur()

                          submit({
                            id,
                            value: e.target.value,
                            calculate_as: fee_template.calculate_as,
                          })
                        }}
                      />
                    ) : (
                      <MaskedTextField
                        mask={percentageMask}
                        label="Valor"
                        className="w-1/2"
                        {...field}
                        value={watch(`contract.contract_fees.${index}.value`) || formatPercentage(value)}
                        errorMessage={errors?.contract?.contract_fees?.[index]?.value?.message || undefined}
                        disabled={updateContractFeeIsPending || privateCompanyIsFetching || disableFields}
                        onBlur={(e) => {
                          field.onBlur()

                          submit({
                            id,
                            value: e.target.value,
                            calculate_as: fee_template.calculate_as,
                          })
                        }}
                      />
                    )}
                  </>
                )}
              />
            </div>
          ))}
        </div>
      }
    />
  )
}
